import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AttributeTypeEnum } from "@sinch/core/entities/serverEnums";
import { AttributeTypeEnum as AttributeType } from "@sinch/core/entities/serverEnums.types";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CountriesList } from "../../CountrySelect/query.graphql";
import { CountriesListQuery } from "../../CountrySelect/query.types";
import { match } from "ts-pattern";
import { IdNamePair } from "@sinch/components/application/StaffDetailPanel/types";
import { filter, find, includes, pluck, prop, propEq } from "ramda";
import { GalleryAttribute } from "@sinch/components/application/Worker/Attributes/GalleryAttribute";
import { useFormat } from "@sinch/core/hooks/format";
import { ensureArray } from "ramda-adjunct";

interface LabelValueProps {
  value: any;
  type?: AttributeTypeEnum | AttributeType | undefined;
  files?: { name: string; url: string }[] | null;
  options?: IdNamePair[];
}

const CountryLabelValue: FC<{ value: string }> = ({ value }) => {
  const { data } = useQueryHandler<CountriesListQuery>(CountriesList);
  const countryName = useMemo(
    () => data?.countries.find((c) => c.code === value)?.name ?? value,
    [data?.countries, value]
  );

  return <Typography variant="descriptionSmall">{countryName}</Typography>;
};

export const AttributeValue: FC<LabelValueProps> = ({ value, type, options, files }) => {
  const { t } = useTranslation();
  const { date } = useFormat();

  const valueNode = match(type)
    .with(AttributeTypeEnum.Bool, () => (
      <Typography variant="descriptionSmall">{value ? t("yes") : t("no")}</Typography>
    ))
    .with(AttributeTypeEnum.Choice, () => {
      if (!options) {
        return null;
      }
      return <Typography variant="descriptionSmall">{findNameById(value, options)}</Typography>;
    })
    .with(AttributeTypeEnum.Country, () => <CountryLabelValue value={value} />)
    .with(AttributeTypeEnum.Multiplechoice, AttributeTypeEnum.Range, () => {
      if (!options) {
        return null;
      }
      return <Typography variant="descriptionSmall">{idToName(value, options).join(", ")}</Typography>;
    })
    .with(AttributeTypeEnum.Date, () => (
      <Typography variant="descriptionSmall">{date.medium(value as string)}</Typography>
    ))
    .with(AttributeTypeEnum.Text, AttributeTypeEnum.Int, AttributeTypeEnum.Textarea, () => (
      <Typography variant="descriptionSmall">{value}</Typography>
    ))
    .with(AttributeTypeEnum.File, () => (
      <>
        {files?.map((file) => (
          <Stack key={file.url} direction="row" spacing={1}>
            <Link href={file.url} target="_blank">
              {t("Action.view")}
            </Link>
            <Divider flexItem orientation="vertical" />
            <Link download={file.name} href={file.url}>
              {t("Action.download")}
            </Link>
          </Stack>
        ))}
      </>
    ))
    .with(AttributeTypeEnum.Image, () => (
      <>
        {files?.map((file) => (
          <Stack key={file.url} direction="row" spacing={1}>
            <Link href={file.url} target="_blank">
              <img alt={file.name} height={64} src={file.url} width={64} />
            </Link>
          </Stack>
        ))}
      </>
    ))
    .with(AttributeTypeEnum.Gallery, () => <GalleryAttribute files={files} />)
    .otherwise(() => <Typography variant="descriptionSmall">{value}</Typography>);

  // The match returns null for value types that have nothing to render (e.g. a choice
  // attribute with no options); render nothing rather than a label with no value below it.
  if (valueNode === null) {
    return null;
  }

  return valueNode;
};

export const idToName = (ids: string[], options: IdNamePair[]) => {
  const stringVals = ensureArray(ids).map(String);
  const filteredOptions = filter((option) => includes(option.id, stringVals), options);
  return pluck("name", filteredOptions);
};

export const findNameById = (id: string, options: IdNamePair[]) => {
  const foundObject = find(propEq("id", String(id)), options ?? []);

  return foundObject ? prop("name", foundObject) : null;
};
